/** Восстановление overlay-сессии: новый AT с demo-server, без soft refresh. */

/** Минимум между запросами нового AT (анти-шторм). */
export const OVERLAY_REISSUE_COOLDOWN_MS = 20_000;

/** Сколько пропущенных PONG подряд → считаем overlay мёртвым. */
export const OVERLAY_PONG_MISS_LIMIT = 5;

/**
 * Сколько миллисекунд без PONG считаем overlay мёртвым.
 * SDK шлёт application PING каждые `pingIntervalSec` (обычно 3 с).
 *
 * @param {number} pingIntervalSec
 * @param {number} [missLimit]
 * @returns {number}
 */
export function overlayPingStaleMs(pingIntervalSec, missLimit = OVERLAY_PONG_MISS_LIMIT) {
  return Math.max(3000, pingIntervalSec * 1000 * missLimit);
}

/**
 * Watchdog по SDK PING/PONG: нет свежего PONG → нужен новый AT.
 *
 * @param {object} [options]
 * @param {number} [options.pingIntervalSec]
 * @param {number} [options.missLimit]
 */
export function createOverlayPingWatchdog(options = {}) {
  const pingIntervalSec = options.pingIntervalSec ?? 3;
  const missLimit = options.missLimit ?? OVERLAY_PONG_MISS_LIMIT;
  const staleMs = overlayPingStaleMs(pingIntervalSec, missLimit);
  let lastPongAtMs = 0;

  return {
    /** Отметить успешный PONG (событие SDK `rttMeasured`). */
    notePong(nowMs = Date.now()) {
      lastPongAtMs = nowMs;
    },
    /** Старт/grace после connect или reissue — ждём первый PONG. */
    noteConnected(nowMs = Date.now()) {
      lastPongAtMs = nowMs;
    },
    /** @returns {boolean} */
    isStale(nowMs = Date.now()) {
      if (!lastPongAtMs) {
        return false;
      }
      return nowMs - lastPongAtMs >= staleMs;
    },
    /** @returns {number} секунд без PONG */
    staleSec(nowMs = Date.now()) {
      if (!lastPongAtMs) {
        return 0;
      }
      return Math.max(0, Math.floor((nowMs - lastPongAtMs) / 1000));
    }
  };
}

/**
 * Нужен ли новый AT по событию SDK.
 * Клиент не различает IP / expired — любой отказ overlay → новый токен.
 *
 * @param {{ type: string, error?: { type?: string } }} event
 * @returns {boolean}
 */
export function overlayTransportNeedsNewToken(event) {
  if (event.type !== "error" || !event.error) {
    return false;
  }
  const kind = event.error.type;
  // reconnect: WS не поднялся со старым AT; server: perimeter прислал ERR:
  return kind === "reconnect" || kind === "server";
}

/**
 * Один reissue за раз + cooldown между попытками.
 *
 * @param {number} [cooldownMs]
 * @returns {{ tryBegin: () => boolean, end: () => void }}
 */
export function createOverlayReissueGuard(cooldownMs = OVERLAY_REISSUE_COOLDOWN_MS) {
  let inFlight = false;
  let lastAtMs = 0;

  return {
    /** @returns {boolean} */
    tryBegin() {
      if (inFlight) {
        return false;
      }
      const now = Date.now();
      if (now - lastAtMs < cooldownMs) {
        return false;
      }
      inFlight = true;
      lastAtMs = now;
      return true;
    },
    end() {
      inFlight = false;
    }
  };
}
